import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomDialog from '@/components/ui/Dialog/Dialog';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import Radio from '@/components/ui/Radio/Radio';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import Fund from '@/models/Fund';
import { downloadFileFromApi } from '@/helpers/downloadFile';
import styles from './SupportCertificatePopup.module.scss';

/**
 * The years a certificate can be issued for: the current one and the one before it. The window
 * rolls on its own every January, which is what SPR-1097 asks for ("2025 drops off and 2027 is
 * added"), and it matches the range the API accepts.
 */
const certificateYears = (): number[] => {
  const currentYear = new Date().getFullYear();
  return [currentYear, currentYear - 1];
};

interface SupportCertificatePopupProps {
  /**
   * The fund the coordinator is working in. Left out on the management screen, where the office
   * issues the certificate across every fund the supported was paid from (HAT-1389).
   */
  fundId?: number;
  supportedId: number;
  supportedName: string;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}

/**
 * Lets a coordinator issue the "אישור על קבלת תמיכות" letter for one of their supporteds: pick a
 * year, pick whether it covers just this fund or every fund they run, and download the PDF.
 * Without a fundId it is the office's version: the year is the only choice, and the letter covers
 * every fund.
 */
const SupportCertificatePopup = ({ fundId, supportedId, supportedName, open, setOpen }: SupportCertificatePopupProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const years = useMemo(certificateYears, []);
  const [year, setYear] = useState(years[0]);
  const [allMyFunds, setAllMyFunds] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  // Raised as its own alert dialog rather than a line inside this one - a message tucked under the
  // form was being missed, and "there is nothing to certify" has to actually stop the coordinator.
  const [alert, setAlert] = useState<{ title: string; message: string } | null>(null);
  const isAllFunds = fundId === undefined;

  // The scope choice is only meaningful for a coordinator who runs more than one fund.
  const { data: funds } = useGetAuthApi<Fund[]>(open && !isAllFunds ? 'funds' : '');
  const hasMultipleFunds = (funds?.length ?? 0) > 1;

  const certificateUrl = isAllFunds
    ? `management/supporteds/${supportedId}/support-certificate?year=${year}`
    : `funds/${fundId}/supporteds/${supportedId}/support-certificate?year=${year}&allMyFunds=${allMyFunds && hasMultipleFunds}`;

  useEffect(() => {
    if (open) {
      setYear(years[0]);
      setAllMyFunds(false);
      setAlert(null);
    }
  }, [open, years]);

  const handleDownload = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    setAlert(null);
    try {
      const downloaded = await downloadFileFromApi(certificateUrl, `אישור תמיכות ${supportedName} ${year}.pdf`, currentUser?.token ?? '');
      if (!downloaded) {
        // The API answers 204 when there is nothing to certify for that year and scope.
        setAlert({
          title: t('supportCertificate.noSupportsTitle'),
          message: t(isAllFunds ? 'supportCertificate.noSupportsAllFunds' : 'supportCertificate.noSupports', { year }),
        });
        return;
      }
      setOpen(false);
    } catch {
      setAlert({
        title: t('supportCertificate.downloadFailedTitle'),
        message: t('supportCertificate.downloadFailed'),
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <CustomDialog
      open={open}
      setOpen={setOpen}
      btnText={isDownloading ? 'supportCertificate.generating' : 'supportCertificate.download'}
      btnDisabled={isDownloading}
      onBtnClick={handleDownload}
    >
      <div className={styles.wrapper}>
        <h3 className={`heading-h4-semibold ${styles.title}`}>{t('supportCertificate.title')}</h3>
        <p className={`body-md-regular ${styles.subtitle}`}>
          {t(isAllFunds ? 'supportCertificate.allFundsSubtitle' : 'supportCertificate.subtitle', { name: supportedName })}
        </p>

        <div className={styles.field}>
          <span className={`body-sm-regular ${styles.label}`}>{t('supportCertificate.year')}</span>
          <div className={styles.options}>
            {years.map((option) => (
              <label key={option} className={styles.option}>
                <Radio checked={year === option} onChange={() => setYear(option)} />
                <span className="body-md-regular">{option}</span>
              </label>
            ))}
          </div>
        </div>

        {!isAllFunds && hasMultipleFunds && (
          <div className={styles.field}>
            <span className={`body-sm-regular ${styles.label}`}>{t('supportCertificate.scope')}</span>
            <div className={styles.options}>
              <label className={styles.option}>
                <Radio checked={!allMyFunds} onChange={() => setAllMyFunds(false)} />
                <span className="body-md-regular">{t('supportCertificate.thisFundOnly')}</span>
              </label>
              <label className={styles.option}>
                <Radio checked={allMyFunds} onChange={() => setAllMyFunds(true)} />
                <span className="body-md-regular">{t('supportCertificate.allMyFunds')}</span>
              </label>
            </div>
          </div>
        )}
      </div>

      <ConfirmationDialog
        open={alert !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setAlert(null);
        }}
        title={alert?.title ?? ''}
        message={alert?.message}
        confirmText="OK"
        withIcon
        hideCancel
        onConfirm={() => setAlert(null)}
      />
    </CustomDialog>
  );
};

export default SupportCertificatePopup;
